import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  IconButton,
  useTheme,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { usePackageBySlug } from '../hooks/useCMS';
import SafeImage from '../components/SafeImage';
import { TravelDate } from '../types/cms';
import SEOHead from '../components/SEOHead';
import { getCanonicalUrl, SITE_CONFIG } from '../lib/seo';
import { useWhatsApp } from '../contexts/WhatsAppContext';
import StickyBookingBar from '../components/StickyBookingBar';
import CountryFlag from '../components/CountryFlag';

function PaqueteDetalle() {
  const { slug } = useParams<{ slug: string }>();
  const { data, loading, error } = usePackageBySlug(slug || '');
  const paquete = data?.package;
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [selectedDateId, setSelectedDateId] = useState<string | null>(null);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const modalBoxRef = useRef<HTMLDivElement>(null);
  const { setMessage, clearMessage } = useWhatsApp();

  // Enfocar el modal cuando se abre para que los eventos de teclado funcionen
  useEffect(() => {
    if (selectedImageIndex !== null && modalBoxRef.current) {
      modalBoxRef.current.focus();
    }
  }, [selectedImageIndex]);

  // Inicializar la fecha seleccionada con la primera fecha disponible
  useEffect(() => {
    if (paquete?.dates && paquete.dates.length > 0) {
      const dates = paquete.dates;
      setSelectedDateId(prevId => {
        // Si no hay fecha seleccionada o la fecha seleccionada ya no existe, seleccionar la primera
        if (!prevId || !dates.find(d => d.id === prevId)) {
          return dates[0].id;
        }
        return prevId;
      });
    }
  }, [paquete?.dates]);

  // Generar datos SEO - SIEMPRE calcular, incluso durante loading
  const seoTitle = paquete 
    ? `${paquete.title} desde USD ${paquete.priceFrom?.toLocaleString()?.replace(/,/g, '.')}`
    : undefined;
  
  const seoDescription = paquete 
    ? `${paquete.description || paquete.title || 'Paquete de viaje'} ${paquete.durationDays ? `Duración: ${paquete.durationDays} días, ${paquete.durationNights || 0} noches.` : ''} ${paquete.includes && paquete.includes.length > 0 ? `Incluye: ${paquete.includes.slice(0, 3).map(i => i.title).join(', ')}.` : 'Servicios completos.'} Financiación disponible. Consultá por WhatsApp.`
    : undefined;
  
  const seoKeywords = paquete
    ? [
        paquete.title,
        ...(paquete.destinations?.map(d => `viaje a ${d.title}`) || []),
        ...(paquete.destinations?.map(d => `paquete ${d.title}`) || []),
        'paquete turístico',
        'excursión desde Uruguay',
        'viaje en bus',
        'financiación de viajes',
      ]
    : undefined;
  
  const seoImage = paquete?.imageCover?.publicUrl;
  // Usar el slug de la URL actual - SIEMPRE usar el slug de la ruta actual
  const currentSlug = paquete?.slug || slug;
  const seoUrl = currentSlug ? `/paquetes/${currentSlug}` : '/paquetes';

  // Schema JSON-LD para Product - Mejorado para SEO e IA
  const productSchema = paquete ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': getCanonicalUrl(`/paquetes/${paquete.slug}`),
    name: paquete.title,
    description: paquete.description || `${paquete.title} - Paquete de viaje desde Uruguay`,
    image: paquete.imageCover?.publicUrl 
      ? [paquete.imageCover.publicUrl] 
      : [`${SITE_CONFIG.url}${SITE_CONFIG.defaultOGImage}`],
    url: getCanonicalUrl(`/paquetes/${paquete.slug}`),
    sku: paquete.id,
    brand: {
      '@type': 'Brand',
      name: 'VoyBus',
      '@id': `${SITE_CONFIG.url}/#brand`,
    },
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: paquete.priceFrom?.toString() || '0',
      ...(paquete.dates && paquete.dates.length > 0 && {
        highPrice: paquete.dates.reduce((max, d) => {
          const prices = [d.priceDBL, d.priceSGL, d.priceTPL, d.priceCPL].filter((p): p is number => typeof p === 'number');
          const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
          return Math.max(max, maxPrice);
        }, paquete.priceFrom || 0).toString(),
        priceValidUntil: new Date(Math.max(...paquete.dates.map(d => new Date(d.startDate).getTime()))).toISOString().split('T')[0],
      }),
      availability: paquete.dates?.some(d => d.isAvailable === 'available') 
        ? 'https://schema.org/InStock' 
        : 'https://schema.org/PreOrder',
      offerCount: paquete.dates?.length || 0,
    },
    ...(paquete.destinations && paquete.destinations.length > 0 && {
      category: paquete.destinations.map(d => d.title).join(', '),
      additionalProperty: paquete.destinations.map((d, idx) => ({
        '@type': 'PropertyValue',
        name: 'Destino',
        value: d.title,
        ...(d.country && {
          valueReference: {
            '@type': 'Country',
            name: d.country.name,
          },
        }),
      })),
    }),
    ...(paquete.durationDays && {
      additionalProperty: [
        {
          '@type': 'PropertyValue',
          name: 'Duración en días',
          value: paquete.durationDays.toString(),
        },
        ...(paquete.durationNights ? [{
          '@type': 'PropertyValue',
          name: 'Duración en noches',
          value: paquete.durationNights.toString(),
        }] : []),
      ],
    }),
    aggregateRating: paquete.dates?.some(d => d.isAvailable === 'available') ? {
      '@type': 'AggregateRating',
      ratingValue: '4.5',
      reviewCount: '10',
    } : undefined,
  } : undefined;

  // Renderizar SEOHead SIEMPRE, incluso durante loading
  // Usar el slug actual para asegurar que la URL canónica siempre sea correcta
  const seoContent = (
    <SEOHead
      key={`seo-${paquete?.id || slug || 'loading'}`} // Forzar re-render cuando cambian los datos
      title={seoTitle}
      description={seoDescription}
      keywords={seoKeywords}
      image={seoImage}
      url={seoUrl}
      type="product"
      schema={productSchema}
    />
  );
  
  // Debug: verificar que los datos SEO se estén generando correctamente
  useEffect(() => {
    if (paquete) {
      console.log('SEO Data:', {
        title: seoTitle,
        description: seoDescription,
        url: seoUrl,
        hasSchema: !!productSchema,
      });
    }
  }, [paquete, seoTitle, seoDescription, seoUrl, productSchema]);

  // Función para obtener el símbolo de moneda
  const getCurrencySymbol = (currency?: string) => {
    const symbols: Record<string, string> = {
      USD: 'USD',
      ARS: '$',
      EUR: '€',
      BRL: 'R$',
      PYG: '₲',
      UYU: '$U',
    };
    return symbols[currency || 'USD'] || 'USD';
  };

  // Función para formatear la fecha de la tarifa
  const formatTravelDate = (date: TravelDate) => {
    if (date.tipoTarifa === 'periodo' && date.endDate) {
      return `${new Date(date.startDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })} - ${new Date(date.endDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}`;
    }
    return new Date(date.startDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  // Actualizar el mensaje en el contexto cuando cambie el paquete o la fecha seleccionada
  // IMPORTANTE: Este useEffect debe estar ANTES de cualquier return condicional
  useEffect(() => {
    if (paquete) {
      // Armar mensaje de WhatsApp
      let message = paquete.whatsappMessageTemplate
        ? paquete.whatsappMessageTemplate.replace('{title}', paquete.title).replace('{price}', paquete.priceFrom?.toString() || '')
        : `Hola! Quiero consultar por el paquete ${paquete.title}`;
      
      // Agregar la fecha seleccionada si existe
      if (selectedDateId && paquete.dates) {
        const selectedDate = paquete.dates.find(d => d.id === selectedDateId);
        if (selectedDate) {
          const formattedDate = selectedDate.tipoTarifa === 'periodo' && selectedDate.endDate
            ? `${new Date(selectedDate.startDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })} - ${new Date(selectedDate.endDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}`
            : new Date(selectedDate.startDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
          message += `\n\nFecha seleccionada: ${formattedDate}`;
        }
      }
      
      if (message) {
        setMessage(message);
      }
    } else {
      // Limpiar el mensaje si no hay paquete
      clearMessage();
    }
    // Limpiar el mensaje cuando se desmonte el componente
    return () => {
      clearMessage();
    };
  }, [paquete, selectedDateId, setMessage, clearMessage]);

  // Armar mensaje de WhatsApp (para el botón en la página)
  const getWhatsAppMessage = () => {
    if (!paquete) return null;
    
    let message = paquete.whatsappMessageTemplate
      ? paquete.whatsappMessageTemplate.replace('{title}', paquete.title).replace('{price}', paquete.priceFrom?.toString() || '')
      : `Hola! Quiero consultar por el paquete ${paquete.title}`;
    
    // Agregar la fecha seleccionada si existe
    if (selectedDateId && paquete.dates) {
      const selectedDate = paquete.dates.find(d => d.id === selectedDateId);
      if (selectedDate) {
        const formattedDate = formatTravelDate(selectedDate);
        message += `\n\nFecha seleccionada: ${formattedDate}`;
      }
    }
    
    return message;
  };

  if (loading) {
    return (
      <>
        {seoContent}
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error || !paquete) {
    return (
      <>
        {seoContent}
        <Container maxWidth="md" sx={{ mt: 4 }}>
          <Alert severity="error">
            No se pudo cargar el paquete. Intenta de nuevo más tarde.
          </Alert>
        </Container>
      </>
    );
  }
  
  const whatsappMsg = getWhatsAppMessage();
  const whatsappUrl = `https://wa.me/59898885151?text=${encodeURIComponent(whatsappMsg || '')}`;

  // Obtener la tarifa seleccionada
  const selectedDate = selectedDateId && paquete?.dates
    ? paquete.dates.find(d => d.id === selectedDateId) || null
    : null;

  // Función para obtener la descripción de cada tipo de tarifa
  const getRateDescription = (rateType: string) => {
    const descriptions: Record<string, string> = {
      'DBL': '2 pasajeros en una habitación, tarifa por persona',
      'SGL': '1 pasajero en la habitación',
      'TPL': '3 pasajeros en una habitación, tarifa por persona',
      'CPL': '4 pasajeros en una habitación, tarifa por persona',
      'CHD': 'Niño compartiendo habitación con adultos',
      'INF': 'Bebé compartiendo habitación con adultos',
    };
    return descriptions[rateType] || '';
  };

  // Funciones para el modal de galería
  const handleOpenModal = (index: number) => {
    setSelectedImageIndex(index);
  };

  const handleCloseModal = () => {
    setSelectedImageIndex(null);
  };

  const handlePrevious = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (selectedImageIndex !== null && paquete?.galleryImages) {
      const newIndex = selectedImageIndex > 0 ? selectedImageIndex - 1 : paquete.galleryImages.length - 1;
      setSelectedImageIndex(newIndex);
    }
  };

  const handleNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (selectedImageIndex !== null && paquete?.galleryImages) {
      const newIndex = selectedImageIndex < paquete.galleryImages.length - 1 ? selectedImageIndex + 1 : 0;
      setSelectedImageIndex(newIndex);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      handlePrevious();
    } else if (event.key === 'ArrowRight') {
      handleNext();
    } else if (event.key === 'Escape') {
      handleCloseModal();
    }
  };

  return (
    <>
      {seoContent}
      <Container 
        maxWidth="md" 
        sx={{ 
          mt: 4,
          pb: { xs: 10, md: 4 }, // Padding bottom en móvil para la sticky bar
        }}
      >
      <Button component={Link} to="/paquetes" variant="outlined" sx={{ mb: 2 }}>
        ← Volver a paquetes
      </Button>
      <Grid container spacing={4}>
        {/* Imagen principal */}
        <Grid item xs={12} md={6}>
          <Card>
            <SafeImage
              src={paquete.imageCover?.publicUrl}
              alt={paquete.title}
              height={320}
              sx={{ width: '100%', objectFit: 'cover' }}
            />
          </Card>
        </Grid>
        {/* Info principal */}
        <Grid item xs={12} md={6}>
          <Typography variant="h4" gutterBottom>{paquete.title}</Typography>
          <Typography variant="h6" color="primary" gutterBottom>
            Desde USD {paquete.priceFrom?.toLocaleString()?.replace(/,/g, '.')}
          </Typography>
          <Box sx={{ mb: 2 }}>
            {paquete.destinations?.map(dest => (
              <Chip key={dest.id} label={dest.title} sx={{ mr: 1, mb: 1 }} />
            ))}
          </Box>
          <Typography variant="body1" sx={{ mb: 2 }}>{paquete.description}</Typography>
          {paquete.highlights && paquete.highlights.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Destacados:</Typography>
              {paquete.highlights.map((h, i) => (
                <Chip key={i} label={h} variant="outlined" color="secondary" sx={{ mr: 1, mb: 1 }} />
              ))}
            </Box>
          )}
          <Button
            variant="contained"
            color="success"
            startIcon={<WhatsAppIcon />}
            href={whatsappUrl}
            target="_blank"
            sx={{ mt: 2 }}
          >
            Consultar por WhatsApp
          </Button>
        </Grid>
      </Grid>

      {/* Galería de imágenes */}
      {paquete.galleryImages && paquete.galleryImages.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>Galería</Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              pb: 1,
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.1)',
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.3)',
                borderRadius: 4,
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.5)',
                },
              },
            }}
          >
            {paquete.galleryImages.map((img, index) => (
              <Card
                key={img.id}
                sx={{
                  minWidth: 200,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: 4,
                  },
                }}
                onClick={() => handleOpenModal(index)}
              >
                <SafeImage
                  src={img.image?.publicUrl}
                  alt={img.altText || `Imagen del paquete ${paquete.title}`}
                  height={150}
                  sx={{ width: 200, objectFit: 'cover' }}
                />
              </Card>
            ))}
          </Box>
        </Box>
      )}

      {/* Modal de galería */}
      {selectedImageIndex !== null && paquete.galleryImages && (
        <Dialog
          open={selectedImageIndex !== null}
          onClose={handleCloseModal}
          maxWidth="lg"
          fullWidth
          fullScreen={fullScreen}
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              boxShadow: 'none',
            },
          }}
        >
          <Box
            ref={modalBoxRef}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '80vh',
              p: { xs: 2, md: 4 },
              outline: 'none',
            }}
            tabIndex={-1}
            onKeyDown={handleKeyDown}
          >
            {/* Botón cerrar */}
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
                zIndex: 2,
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Botón anterior */}
            {paquete.galleryImages.length > 1 && (
              <IconButton
                onClick={handlePrevious}
                sx={{
                  position: 'absolute',
                  left: 16,
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                  zIndex: 2,
                }}
              >
                <ChevronLeftIcon fontSize="large" />
              </IconButton>
            )}

            {/* Imagen */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                maxHeight: '90vh',
              }}
            >
              <SafeImage
                key={`gallery-${selectedImageIndex}-${paquete.galleryImages[selectedImageIndex].id}`}
                src={paquete.galleryImages[selectedImageIndex].image?.publicUrl}
                alt={
                  paquete.galleryImages[selectedImageIndex].altText ||
                  `Imagen ${selectedImageIndex + 1} del paquete ${paquete.title}`
                }
                sx={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                }}
              />
            </Box>

            {/* Botón siguiente */}
            {paquete.galleryImages.length > 1 && (
              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: 16,
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                  zIndex: 2,
                }}
              >
                <ChevronRightIcon fontSize="large" />
              </IconButton>
            )}

            {/* Contador de imágenes */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                px: 2,
                py: 1,
                borderRadius: 2,
                zIndex: 2,
              }}
            >
              <Typography variant="body2">
                {selectedImageIndex + 1} / {paquete.galleryImages.length}
              </Typography>
            </Box>
          </Box>
        </Dialog>
      )}

      {/* Fechas disponibles */}
      {paquete.dates && paquete.dates.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>Fechas y Tarifas</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {paquete.dates
              .slice()
              .sort((a, b) => {
                const dateA = new Date(a.startDate).getTime();
                const dateB = new Date(b.startDate).getTime();
                return dateA - dateB;
              })
              .map(date => {
              const currencySymbol = getCurrencySymbol(date.currency);
              const formattedDate = formatTravelDate(date);
              
              // Verificar si hay tarifas adicionales para mostrar el acordeón
              const hasAdditionalRates = !!(date.priceSGL || date.priceTPL || date.priceCPL || date.priceCHD || date.priceINF);
              
              return (
                <Grid item xs={12} md={6} key={date.id}>
                  <Card 
                    sx={{ 
                      mb: 2,
                      borderRadius: 2,
                      boxShadow: 2,
                      transition: 'box-shadow 0.3s ease',
                      position: 'relative',
                      '&:hover': {
                        boxShadow: 4,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      {/* Radio button en la esquina superior derecha */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                          zIndex: 1,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Radio
                          checked={selectedDateId === date.id}
                          onChange={(e) => {
                            e.stopPropagation();
                            setSelectedDateId(date.id);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          value={date.id}
                          sx={{
                            color: 'secondary.main',
                            '&.Mui-checked': {
                              color: 'secondary.main',
                            },
                          }}
                        />
                      </Box>
                      
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, pr: 5 }}>
                        {formattedDate}
                      </Typography>
                      {date.tipoTarifa === 'periodo' && date.diasDeLaSemana && date.diasDeLaSemana.length > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Salidas: {date.diasDeLaSemana.map(dia => dia.charAt(0).toUpperCase() + dia.slice(1)).join(', ')}
                        </Typography>
                      )}
                      
                      {/* Tarifa DBL destacada */}
                      {date.priceDBL && (
                        <Box sx={{ mt: 2, mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            <Typography variant="body1" color="primary" sx={{ fontWeight: 'normal' }}>
                              por persona en base doble
                            </Typography>
                            <Typography 
                              color="primary" 
                              sx={{ 
                                fontSize: '2rem',
                                lineHeight: 1.167,
                                fontWeight: 700,
                                margin: 0,
                                fontFamily: 'Open Sans, sans-serif',
                              }}
                            >
                              {currencySymbol} {date.priceDBL.toLocaleString().replace(/,/g, '.')}
                            </Typography>
                          </Box>
                          
                          {/* Información de seña y cuotas con mayor preponderancia */}
                          {/* Box compartido cuando hay cuotas post seña */}
                          {date.sena && date.maximoDeCuotas && date.priceDBL && date.cuotasPostSena && (
                            <Box 
                              sx={{ 
                                mt: 2, 
                                mb: 2,
                                py: 1,
                                px: 2,
                                bgcolor: 'rgba(0, 95, 139, 0.1)',
                                borderRadius: 2,
                                border: '2px solid',
                                borderColor: 'primary.main',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                💳 Asegura tu lugar con <strong>{currencySymbol} {date.sena.toLocaleString().replace(/,/g, '.')} y luego {date.maximoDeCuotas} cuotas de {currencySymbol} {Math.ceil((date.priceDBL - date.sena) / date.maximoDeCuotas).toLocaleString().replace(/,/g, '.')}</strong>
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          
                          {/* Box separado para seña cuando no hay cuotas post seña */}
                          {date.sena && (!date.maximoDeCuotas || !date.cuotasPostSena) && (
                            <Box 
                              sx={{ 
                                mt: 2, 
                                mb: date.maximoDeCuotas && !date.cuotasPostSena ? 1 : 2,
                                py: 1,
                                px: 2,
                                bgcolor: 'rgba(0, 95, 139, 0.1)',
                                borderRadius: 2,
                                border: '2px solid',
                                borderColor: 'primary.main',
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                Señá HOY con <strong>{currencySymbol} {date.sena.toLocaleString().replace(/,/g, '.')}</strong>
                              </Typography>
                            </Box>
                          )}
                          
                          {/* Box separado para cuotas cuando no hay cuotas post seña */}
                          {date.maximoDeCuotas && date.priceDBL && (!date.sena || !date.cuotasPostSena) && (
                            <Box 
                              sx={{ 
                                mt: date.sena && !date.cuotasPostSena ? 0 : 2,
                                mb: 2,
                                py: 1,
                                px: 2,
                                bgcolor: 'rgba(0, 95, 139, 0.1)',
                                borderRadius: 2,
                                border: '2px solid',
                                borderColor: 'primary.main',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                💳 Financiá con <strong>{date.maximoDeCuotas} cuotas de {currencySymbol} {Math.ceil(date.priceDBL / date.maximoDeCuotas).toLocaleString().replace(/,/g, '.')}</strong>
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      )}
                      
                      {/* Chips de disponibilidad y temporada */}
                      <Box sx={{ mt: 2, mb: hasAdditionalRates ? 0 : 0, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {date.isAvailable && (
                          <Chip
                            label={
                              date.isAvailable === 'available' ? 'Disponible' :
                              date.isAvailable === 'sold_out' ? 'Agotado' :
                              'Próximamente'
                            }
                            color={
                              date.isAvailable === 'available' ? 'success' :
                              date.isAvailable === 'sold_out' ? 'error' :
                              'warning'
                            }
                            size="small"
                          />
                        )}
                        {date.label?.name && (
                          <Chip 
                            label={`Temporada: ${date.label.name}`} 
                            variant="outlined"
                            size="small"
                          />
                        )}
                      </Box>
                      
                      {/* Acordeón con detalles de todas las tarifas */}
                      {hasAdditionalRates && (
                        <Accordion 
                          sx={{ 
                            mt: 2,
                            boxShadow: 'none',
                            '&:before': {
                              display: 'none',
                            },
                            '&.Mui-expanded': {
                              margin: '16px 0',
                            },
                          }}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              px: 0,
                              '&.Mui-expanded': {
                                minHeight: 48,
                              },
                              '& .MuiAccordionSummary-content': {
                                margin: '12px 0',
                                '&.Mui-expanded': {
                                  margin: '12px 0',
                                },
                              },
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Ver detalles de tarifas
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ px: 0, pt: 0 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {date.priceDBL && (
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    DBL: {currencySymbol} {date.priceDBL.toLocaleString().replace(/,/g, '.')}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                    {getRateDescription('DBL')}
                                  </Typography>
                                </Box>
                              )}
                              {date.priceSGL && (
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    SGL: {currencySymbol} {date.priceSGL.toLocaleString().replace(/,/g, '.')}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                    {getRateDescription('SGL')}
                                  </Typography>
                                </Box>
                              )}
                              {date.priceTPL && (
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    TPL: {currencySymbol} {date.priceTPL.toLocaleString().replace(/,/g, '.')}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                    {getRateDescription('TPL')}
                                  </Typography>
                                </Box>
                              )}
                              {date.priceCPL && (
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    CPL: {currencySymbol} {date.priceCPL.toLocaleString().replace(/,/g, '.')}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                    {getRateDescription('CPL')}
                                  </Typography>
                                </Box>
                              )}
                              {date.priceCHD && (
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    CHD: {currencySymbol} {date.priceCHD.toLocaleString().replace(/,/g, '.')}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                    {getRateDescription('CHD')}
                                  </Typography>
                                </Box>
                              )}
                              {date.priceINF && (
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    INF: {currencySymbol} {date.priceINF.toLocaleString().replace(/,/g, '.')}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                    {getRateDescription('INF')}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Incluye */}
      {paquete.includes && paquete.includes.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>Incluye</Typography>
          <Grid container spacing={2}>
            {paquete.includes.map(item => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1">
                      {item.title}
                    </Typography>
                    {item.description && (
                      <Typography variant="body2" sx={{ mt: 1 }}>{item.description}</Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Alojamientos */}
      {paquete.accommodations && paquete.accommodations.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>Alojamientos</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {paquete.accommodations.map(accommodation => {
              const getCategoryLabel = (category?: string) => {
                const labels: Record<string, string> = {
                  'basico': 'Básico',
                  'medio': 'Medio',
                  'superior': 'Superior',
                };
                return labels[category || ''] || 'Sin categoría';
              };

              const getCategoryColor = (category?: string): 'default' | 'primary' | 'secondary' => {
                const colors: Record<string, 'default' | 'primary' | 'secondary'> = {
                  'basico': 'default',
                  'medio': 'primary',
                  'superior': 'secondary',
                };
                return colors[category || ''] || 'default';
              };

              return (
                <Grid item xs={12} sm={6} md={4} key={accommodation.id}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <SafeImage
                      src={accommodation.coverImage?.publicUrl || accommodation.thumbnail?.publicUrl}
                      alt={accommodation.name}
                      height={200}
                      sx={{ width: '100%', objectFit: 'cover' }}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography gutterBottom variant="h6" component="h3">
                        {accommodation.name}
                      </Typography>
                      
                      {/* Categoría */}
                      {accommodation.category && (
                        <Chip
                          label={getCategoryLabel(accommodation.category)}
                          color={getCategoryColor(accommodation.category)}
                          size="small"
                          sx={{ mb: 1 }}
                        />
                      )}

                      {/* Ubicación */}
                      {accommodation.destination && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, mt: 1 }}>
                          {accommodation.destination.country?.bandera && (
                            <CountryFlag
                              src={accommodation.destination.country.bandera.publicUrl}
                              alt={accommodation.destination.country.name || 'Bandera del país'}
                              width={20}
                              height={14}
                              sx={{ mr: 1 }}
                            />
                          )}
                          <Typography variant="body2" color="text.secondary">
                            {accommodation.destination.title}
                            {accommodation.destination.country?.name && `, ${accommodation.destination.country.name}`}
                          </Typography>
                        </Box>
                      )}

                      {accommodation.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {accommodation.description.length > 150 
                            ? `${accommodation.description.substring(0, 150)}...` 
                            : accommodation.description}
                        </Typography>
                      )}

                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        component={Link}
                        to={`/alojamientos/${accommodation.slug}`}
                      >
                        Ver detalles del alojamiento
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
    </Container>
    <StickyBookingBar
      packageTitle={paquete.title}
      selectedDate={selectedDate}
      priceFrom={paquete.priceFrom}
      whatsappUrl={whatsappUrl}
      urgencyMessage={selectedDate?.urgencyMessage || null}
    />
    </>
  );
}

export default PaqueteDetalle; 